import React, { useState } from "react"
import AuthLayoutTitle from "../../../components/title/AuthLayoutTitle"
import ResetOTP from "../../../components/auth/ResetOTP"
import { useUserActions } from "../../../store/user/userAction"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectUser, selectUserSlice } from "../../../store/user/userSlice"
import { errorAlert } from "../../../utils/alerts"
import { verifyEmailRedirectDestination } from "../../../utils/safeReturnPath"

const AUTO_OTP_COOLDOWN_MS = 5000
const PROFILE_VERIFY_SUCCESS_FLAG = "pw_profile_verify_success"

export default function VerifyEmail(): JSX.Element {
    const { userLogout, sendOTP, verifyEmail } = useUserActions()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const user = useSelector(selectUser)
    const { accessToken } = useSelector(selectUserSlice)

    const afterVerifyPath = () =>
        verifyEmailRedirectDestination(searchParams.get("returnTo"))

    const [isSent, setIsSent] = React.useState(false)
    const [otpValues, setOtpValues] = React.useState("")
    const [loading, setLoading] = useState(false)

    React.useEffect(() => {
        if (!user && !accessToken) {
            navigate("/auth/login")
            return
        }
        if (user?.emailVerified) {
            navigate(afterVerifyPath(), { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, accessToken, searchParams])

    /** Send OTP on entry (e.g. from Settings); cooldown avoids duplicate sends in React Strict Mode. */
    React.useEffect(() => {
        if (!user?.email || user.emailVerified) return

        const key = `pw_verify_email_otp_auto_${user.email}`
        const last = Number(sessionStorage.getItem(key) || 0)
        if (Date.now() - last < AUTO_OTP_COOLDOWN_MS) return

        let cancelled = false
        ;(async () => {
            setLoading(true)
            try {
                const response = await sendOTP({ email: user.email })
                if (cancelled) return
                sessionStorage.setItem(key, String(Date.now()))
                if (response?.data?.status) {
                    setIsSent(true)
                } else {
                    errorAlert(
                        response?.data?.message ||
                            "Could not send verification code"
                    )
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.email, user?.emailVerified])

    const handleSendCode = async () => {
        setLoading(true)

        if (user) {
            const data = {
                email: user.email,
            }
            const response = await sendOTP(data)

            if (response.data.status) {
                setIsSent(true)
            } else {
                // fire alert
                errorAlert(response.data.message)
            }
        }

        setLoading(false)
    }

    const handleVerifyEmail = async ({ setLoading: setBtnLoading }) => {
        if (user) {
            const data = {
                otp: otpValues,
                email: user.email,
            }
            setBtnLoading(true)
            const response = await verifyEmail(data)
            setBtnLoading(false)
            if (response.data.status) {
                const to = afterVerifyPath()
                const fromSettings =
                    verifyEmailRedirectDestination(
                        searchParams.get("returnTo")
                    ).split("?")[0] === "/settings"
                if (fromSettings) {
                    sessionStorage.setItem(PROFILE_VERIFY_SUCCESS_FLAG, "1")
                }
                navigate(to, { replace: true })
            } else {
                errorAlert(response.data.message)
            }
        }
    }

    return (
        <div className="h-full flex items-center flex-col gap-16 w-full md:w-[30rem]">
            <div className="flex flex-col gap-3 ">
                <AuthLayoutTitle title="Verify Email" />
                {/* <AuthHeader
                    title={`OTP sent successfully to ${user?.email}, Please check your email`}
                /> */}
                <h2 className="text-center text-sm text-gray-300">
                    OTP sent successfully to{" "}
                    <span className="font-semibold">{user?.email}</span>
                    <br /> Please check your email
                </h2>
            </div>
            <div className="flex flex-col gap-2 items-center">
                <ResetOTP
                    handleContinueClick={handleVerifyEmail}
                    handleSendAgainClick={handleSendCode}
                    otpValues={otpValues}
                    setOtpValues={setOtpValues}
                    loadershow={loading}
                />
                {isSent && (
                    <span className="text-greenSecondary text-xs">
                        OTP successfully Sent to {user?.email}
                    </span>
                )}
            </div>
            <button onClick={userLogout}>
                {"<"}&nbsp; <button className="underline">Go Back</button>
            </button>
        </div>
    )
}
